import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  AdditionalMailingAudience,
  AdditionalMailingType,
  IAdditionalMailing,
} from "src/store/additionalMailing/types";
import {
  AdditionalMailingsView,
  ADDITIONAL_MAILINGS_VIEW,
} from "./AdditionalMailings";
import {
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
  ADDITIONAL_MAILING_TYPE_LABEL,
} from "src/constants/labels/additionalMailingLabel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const PENDING_APPROVAL_COLUMNS = [
  "issuer",
  "type",
  "recordDate",
  "dateReceived",
  "solicitor",
  "audience",
];

export const RECENTLY_APPROVED_COLUMNS = [
  "issuer",
  "type",
  "recordDate",
  "dateReceived",
  "solicitor",
  "audience",
];

export const HISTORICAL_COLUMNS = [
  "issuer",
  "type",
  "recordDate",
  "dateReceived",
  "solicitor",
  "audience",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type AdditionalMailingColumnData = {
  issuer: ColumnType;
  type: ColumnType;
  recordDate: ColumnType;
  dateReceived: ColumnType;
  solicitor: ColumnType;
  audience: ColumnType;
};

export function getColumns(view: AdditionalMailingsView) {
  if (view === ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL) {
    return PENDING_APPROVAL_COLUMNS;
  } else if (view === ADDITIONAL_MAILINGS_VIEW.RECENTLY_APPROVED) {
    return RECENTLY_APPROVED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData(
  _view: string,
  { getSort, onOrderingChange }: ColumnDataHelpers
): AdditionalMailingColumnData {
  return {
    issuer: {
      sort: getSort("issuer__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
      style: "text",
      title: "Issuer",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    type: {
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
    },
    recordDate: {
      sort: getSort("proxy_event__record_date"),
      sortFn: (data: unknown) =>
        onOrderingChange(data, "proxy_event__record_date"),
      style: "text",
      title: "Record Date",
    },
    dateReceived: {
      sort: getSort("date_received"),
      sortFn: (data: unknown) => onOrderingChange(data, "date_received"),
      style: "text",
      title: "Date Received",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    audience: {
      sort: getSort("audience"),
      sortFn: (data: unknown) => onOrderingChange(data, "audience"),
      style: "text",
      title: "Audience",
    },
  };
}

export function getRowData(
  _view: string,
  events: IAdditionalMailing[],
  onRowClick: (event: IAdditionalMailing) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: IAdditionalMailing;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const { id, type, issuer, proxyEvent, dateReceived, solicitor, audience } =
      event;

    return {
      id,
      data: { event, idx },
      rowData: {
        issuer: {
          content: issuer.name,
          clickHandler: handleRowClick,
        },
        type: {
          content:
            ADDITIONAL_MAILING_TYPE_LABEL[type as AdditionalMailingType] ||
            type,
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: dayjs(proxyEvent.recordDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        dateReceived: {
          content: dayjs(dateReceived).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor?.name || "-",
          clickHandler: handleRowClick,
        },
        audience: {
          content:
            ADDITIONAL_MAILING_AUDIENCE_LABEL[
              audience as AdditionalMailingAudience
            ] || audience,
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
